import React from 'react';
import { Link } from 'react-router-dom';
import { UIMessage } from './types/chat.types';
import { parseInlineRefs } from './utils/parseInlineRefs';
import { parseMarkdownLinks } from './utils/parseMarkdownLinks';

interface Props {
  message: UIMessage;
  avatarEmoji?: string;
  /** Optional click handler for an inline [[ref:cart_item_id|name]] chip.
   *  Default behaviour scrolls to the matching data-cart-item-id element. */
  onRefClick?: (cartItemId: string) => void;
  /** Optional click handler for a markdown [label](url) link. Return `true`
   *  to indicate the click was handled — the default navigation is then
   *  prevented. Falsy return falls through to the normal href / Link route. */
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean;
}

function renderTextWithLinks(
  text: string,
  keyBase: string,
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean,
): React.ReactNode {
  const segs = parseMarkdownLinks(text);
  return segs.map((seg, i) => {
    if (seg.type === 'text') {
      return <React.Fragment key={`${keyBase}-t${i}`}>{seg.value}</React.Fragment>;
    }
    // Relative paths use react-router <Link>; absolute URLs open new tab.
    const isAbsolute = /^https?:\/\//i.test(seg.url);
    const handle = (e: React.MouseEvent) => {
      if (onLinkClick && onLinkClick(seg.url, e)) {
        e.preventDefault();
      }
    };
    if (isAbsolute) {
      return (
        <a
          key={`${keyBase}-l${i}`}
          className="bot-bubble__link"
          href={seg.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handle}
        >
          {seg.label}
        </a>
      );
    }
    return (
      <Link key={`${keyBase}-l${i}`} className="bot-bubble__link" to={seg.url} onClick={handle}>
        {seg.label}
      </Link>
    );
  });
}

function renderInlineRefs(
  text: string,
  onRefClick?: (id: string) => void,
  onLinkClick?: (url: string, e: React.MouseEvent) => boolean,
): React.ReactNode {
  const segments = parseInlineRefs(text);
  return segments.map((seg, i) => {
    if (seg.type === 'text') {
      return (
        <React.Fragment key={i}>
          {renderTextWithLinks(seg.value, `r${i}`, onLinkClick)}
        </React.Fragment>
      );
    }
    return (
      <button
        key={i}
        type="button"
        className="bot-bubble__ref"
        title={seg.displayName}
        onClick={() => {
          if (onRefClick) return onRefClick(seg.cartItemId);
          // Default: scroll to a card rendered with the same cart_item_id
          // (T5 will render <CartPreview> cards bearing this attribute).
          const target = document.querySelector(
            `[data-cart-item-id="${CSS.escape(seg.cartItemId)}"]`,
          );
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      >
        {seg.displayName}
      </button>
    );
  });
}

const MessageBubble: React.FC<Props> = ({
  message,
  avatarEmoji = '🤖',
  onRefClick,
  onLinkClick,
}) => {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const showRefs = !isUser && !isSystem;

  return (
    <div className={`bot-bubble bot-bubble--${message.role}`}>
      {!isUser && !isSystem && <div className="bot-bubble__avatar">{avatarEmoji}</div>}
      <div className="bot-bubble__content">
        <div className="bot-bubble__text">
          {showRefs
            ? renderInlineRefs(message.text, onRefClick, onLinkClick)
            : message.text}
          {message.isStreaming && <span className="bot-bubble__cursor" />}
        </div>
        {!message.isStreaming && (
          <div className="bot-bubble__time">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
